import EventLibrarySelect, { DeleteConfirmModal, SuccessPopup, deletedMessage } from "./EventLibrarySelect";
import { CmpEventTicketAudienceEnum, CmpEventLibraryKindEnum, CmpEventSponsorshipTaxModeEnum } from "../../../../../graphql/types";
import React, { useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import RichTextEditor from "../../../../common/FormComponents/RichTextEditor";
import { ConfirmDialog, ResultModal } from "../../EventSales/eventSalesShared";
import {
  EventSponsorshipAddOn,
  EventSponsorshipPackage,
  EventTicketType,
} from "../../eventMockData";
import {
  FieldLabel,
  WizardActionButton,
  WizardDialogHeader,
  WizardStepProps,
  wizardCurrencyAdornmentClassName,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import { useWizardDialogUnsavedGuard } from "./wizardDialogUnsavedGuard";
import { EventReorderHandle, eventReorderTableRowProps, useEventRowReorder } from "./useEventRowReorder";
import { CurrencyInput } from "../../../../common/dashboard/CurrencyInput";
import { formatCurrencyValueOnBlur } from "../../../../common/dashboard/currencyUnits";
import { enterFieldMessage } from "../../../../../utils/fieldMessages";

// Claude Design (15) Sponsorship Settings (CES:866-877, Monday 13252617096,
// decision Q6): sponsorship sales are taxed (Full) unless the second box is
// ticked (None). The third box, at page level, charges tax on complimentary
// tickets for every level that includes tickets (IncludedComponents); each
// such level carries the member ticket value. A level is never saved with the
// value and 0 tickets (Ajax OLG invoice #233, Oct 9 2026).
const levelHasTickets = (pkg: { includedTickets?: string }) => Number(pkg.includedTickets) > 0;
const withComplimentaryTicketTax = <T extends { includedTickets?: string; includedTicketValue?: string }>(
  packages: ReadonlyArray<T>,
  charge: boolean,
  memberTicketValue: string,
): T[] => packages.map((pkg) => ({ ...pkg, includedTicketValue: charge && levelHasTickets(pkg) ? memberTicketValue : "" }));
const memberTicketPrice = (tickets: ReadonlyArray<{ code?: string; name: string; price: string; memberOnly?: boolean }>): string => {
  const memberTicket =
    tickets.find((ticket) => (ticket.code || "").toUpperCase() === "MT" || /^member ticket$/i.test(ticket.name.trim())) ||
    tickets.find((ticket) => ticket.memberOnly && Number(ticket.price) > 0);
  return memberTicket && Number(memberTicket.price) > 0 ? formatCurrencyValueOnBlur(memberTicket.price) : "";
};

const formatDollars = (value?: string): string => {
  const numeric = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return `$${(Number.isFinite(numeric) ? numeric : 0).toFixed(2)}`;
};

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const findNoChargeSponsorshipTicket = (
  tickets: EventTicketType[]
): EventTicketType | undefined => tickets.find((ticket) => ticket.code === "NCST" && Number(ticket.price) === 0 && !ticket.memberOnly);

const noChargeTicket = (id: string): EventTicketType => ({ id, code: "NCST", name: "No Charge Sponsorship Ticket",
  description: "No charge tickets allocated with sponsorship packages", price: "0.00", attendeesPerTicket: "1",
  memberOnly: false, audience: CmpEventTicketAudienceEnum.SponsorIncluded, visibleOnRegistrationForm: false });

const blankPackage = (tickets: EventTicketType[]): EventSponsorshipPackage => ({
  id: wizardId("sponsor"),
  name: "",
  description: "",
  price: "",
  maxAvailable: "0",
  includedTickets: "0",
  publicDisplayEnabled: true,
  websiteListingEnabled: true,
  code: "",
  heroBannerEnabled: false,
  additionalBenefits: "",
  addOns: [],
  includedTicketTypeId: findNoChargeSponsorshipTicket(tickets)?.id || wizardId("sponsor-ticket"),
});

const totalAllocated = (pkg: EventSponsorshipPackage) => {
  const available = Number(pkg.maxAvailable) || 0;
  const included = Number(pkg.includedTickets) || 0;
  return available * included;
};

const QuantityStepper = ({
  value,
  onChange,
  ariaLabel,
  disabled = false,
  error = false,
}: {
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  error?: boolean;
}): React.ReactElement => {
  const current = Number(value) || 0;
  return (
    <div
      className={`flex h-[42px] items-center justify-between rounded-[8px] border ${error ? "border-red-500" : "border-[#9E9E9E]"} ${
        disabled ? "bg-gray-50" : "bg-white"
      }`}
    >
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={`Decrease ${ariaLabel}`}
        onClick={() => onChange(String(Math.max(0, current - 1)))}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <input aria-label={ariaLabel} inputMode="numeric" disabled={disabled} value={value || "0"}
        className="min-w-0 w-full bg-transparent text-center text-[14px] font-normal outline-none disabled:text-gray-400"
        onChange={({ target }) => { if (/^\d*$/.test(target.value)) onChange(target.value); }} />
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={`Increase ${ariaLabel}`}
        onClick={() => onChange(String(current + 1))}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

const levelTableHeadCellClassName =
  "!text-[11px] !font-semibold !uppercase !tracking-[.05em] !text-[#0A0A0A]";
const levelTableActionClassName = (background: string) =>
  `rounded-[6px] border-0 px-[12px] py-[7px] text-[11px] font-semibold text-white ${background}`;

const Step4SponsorSetup = ({
  chamberId,
  event,
  setEvent,
  onDialogSaved,
}: WizardStepProps): React.ReactElement => {
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));

  const packages = event.sponsorshipPackages;
  const noSponsorshipTax = event.sponsorshipTaxMode !== undefined && event.sponsorshipTaxMode !== CmpEventSponsorshipTaxModeEnum.Full;
  // An old event with mixed levels shows the box ticked when any level taxes
  // its complimentary tickets (decision Q6); its levels change only when staff
  // change this box.
  const chargesComplimentaryTicketTax = event.sponsorshipTaxMode === CmpEventSponsorshipTaxModeEnum.IncludedComponents;
  const levelMemberTicketValue = memberTicketPrice(event.tickets);
  const setNoSponsorshipTax = (checked: boolean) => setEvent((previous) => ({
    ...previous,
    sponsorshipTaxMode: checked ? CmpEventSponsorshipTaxModeEnum.None : CmpEventSponsorshipTaxModeEnum.Full,
    sponsorshipPackages: checked ? previous.sponsorshipPackages : withComplimentaryTicketTax(previous.sponsorshipPackages, false, ""),
  }));
  const setComplimentaryTicketTax = (checked: boolean) => setEvent((previous) => ({
    ...previous,
    sponsorshipTaxMode: checked ? CmpEventSponsorshipTaxModeEnum.IncludedComponents : CmpEventSponsorshipTaxModeEnum.None,
    sponsorshipPackages: withComplimentaryTicketTax(previous.sponsorshipPackages, checked, memberTicketPrice(previous.tickets)),
  }));
  const [draft, setDraft] = useState<EventSponsorshipPackage | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EventSponsorshipPackage | null>(null);
  const [addOnEntry, setAddOnEntry] = useState<EventSponsorshipAddOn | null>(null);
  const [pendingAddOnRemoval, setPendingAddOnRemoval] = useState<EventSponsorshipAddOn | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [showAddOnValidation, setShowAddOnValidation] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);
  const [deletedRow, setDeletedRow] = useState<{ message: string; highlights: string[] } | null>(null);
  // Both step-4 tables reorder by their "⋮⋮" handle (CES:890, :1566).
  const reorderLevelRow = useEventRowReorder(packages, (next) => update("sponsorshipPackages", next));

  // Confirm the public sales change while keeping saved levels and records.
  const handleSponsorshipToggle = (checked: boolean) => {
    if (!checked && packages.length > 0) {
      setConfirmDisableOpen(true);
      return;
    }
    update("sponsorshipEnabled", checked);
  };

  // Claude Design (8) moved website display to each sponsor level (decision
  // Q14 A, Create Event Screen.dc.html:1440-1451): the event-level box is gone
  // and eventBackendMappers folds an event saved with it ticked into every
  // level (decision Q15). New levels start shown (blankPackage).

  const closeDraft = () => {
    setDraft(null);
    setAddOnEntry(null);
    setShowValidation(false);
    setShowAddOnValidation(false);
  };
  const openDraft = (nextDraft: EventSponsorshipPackage, nextIsNew: boolean) => {
    setDraft(nextDraft);
    setIsNew(nextIsNew);
    setAddOnEntry(null);
    setShowValidation(false);
    setShowAddOnValidation(false);
    unsavedGuard.snapshot(nextDraft);
  };
  // Create Event Screen.dc.html:1754: a new level starts with the hero off.
  const openAdd = () => openDraft(blankPackage(event.tickets), true);
  const openEdit = (pkg: EventSponsorshipPackage) =>
    openDraft({
      ...pkg,
      includedTicketTypeId: pkg.includedTicketTypeId || findNoChargeSponsorshipTicket(event.tickets)?.id || wizardId("sponsor-ticket"),
      addOns: (pkg.addOns || []).map((addOn) => ({ ...addOn })),
    }, false);
  const patchDraft = (patch: Partial<EventSponsorshipPackage>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const draftAddOns = draft?.addOns || [];
  const reorderAddOnRow = useEventRowReorder(draftAddOns, (addOns) => patchDraft({ addOns }));
  const assignedTicket = event.tickets.find(
    (ticket) => ticket.id === draft?.includedTicketTypeId
  ) || (draft?.includedTicketTypeId && !packages.some(pkg => pkg.includedTicketTypeId === draft.includedTicketTypeId) ? noChargeTicket(draft.includedTicketTypeId) : undefined);

  // Design CES:1453-1460 + 2803-2804: one Title Sponsor level per event. The
  // box is disabled while another level holds it.
  const heroOwner = packages.find((pkg) => pkg.heroBannerEnabled && pkg.id !== draft?.id);
  const heroTaken = Boolean(heroOwner) && !draft?.heroBannerEnabled;
  // The Title Sponsor is exclusive (CES:1455, Moeen Q7): turning it on sets one
  // spot. A level saved earlier with more spots keeps them until staff edit it.
  const titleSponsorSpotsLocked = Boolean(draft?.heroBannerEnabled) && draft?.maxAvailable === "1";
  const titleSponsorSpotsNote = Boolean(draft?.heroBannerEnabled) && draft?.maxAvailable !== "1";
  const handleHeroBannerToggle = (checked: boolean) => {
    if (checked && heroOwner) return;
    patchDraft(checked ? { heroBannerEnabled: true, maxAvailable: "1" } : { heroBannerEnabled: false });
  };

  // Design (onSmAddonSave): code and description are required on click; the
  // code is stored in upper case.
  const addOnCodeMissing = Boolean(addOnEntry && !addOnEntry.code?.trim());
  const addOnDescriptionMissing = Boolean(addOnEntry && !addOnEntry.description.trim());
  const saveAddOnEntry = () => {
    if (!addOnEntry) {
      return;
    }
    if (addOnCodeMissing || addOnDescriptionMissing) {
      setShowAddOnValidation(true);
      return;
    }
    patchDraft({ addOns: [...draftAddOns, { ...addOnEntry, code: (addOnEntry.code || "").trim().toUpperCase(), description: addOnEntry.description.trim() }] });
    setAddOnEntry({ id: wizardId("addon"), description: "", code: "", quantity: "0" });
    setShowAddOnValidation(false);
  };

  // saveSponsorModal (Create Event Screen.dc.html:1757-1762): Sponsor Code is
  // optional, so only spots, name, price and benefits are required.
  const draftValid = Boolean(
    draft &&
      Number.isInteger(Number(draft.maxAvailable)) && Number(draft.maxAvailable) > 0 &&
      draft.name.trim() &&
      draft.price.trim() && Number.isFinite(Number(draft.price)) && Number(draft.price) >= 0 &&
      stripHtml(draft.additionalBenefits || "")
  );

  const saveDraft = () => {
    if (!draft) {
      return;
    }
    if (!draftValid) {
      setShowValidation(true);
      return;
    }
    // POL-005: keep the canonical decimal-dollars shape ("500.00", no "$") in
    // form state; formatDollars stays for read-only display sites only.
    const existing = event.sponsorshipPackages.find((pkg) => pkg.id === draft.id);
    const normalized = {
      ...draft,
      price: formatCurrencyValueOnBlur(draft.price),
      includedTickets: draft.includedTickets,
      // Page-level complimentary-ticket tax (decision Q6): a level without
      // tickets never carries the value; a new level, or one that gains
      // tickets, gets the member ticket value; an existing value is kept.
      includedTicketValue: !levelHasTickets(draft) ? ""
        : chargesComplimentaryTicketTax && (!existing || !levelHasTickets(existing) || !Number(draft.includedTicketValue))
          ? levelMemberTicketValue
          : draft.includedTicketValue,
      includedTicketTypeId: Number(draft.includedTickets) > 0 ? draft.includedTicketTypeId : undefined,
    };
    if (Number(normalized.includedTickets) > 0 && !assignedTicket) { setShowValidation(true); return; }
    setEvent(previous => {
      const sponsorshipPackages = isNew ? [...previous.sponsorshipPackages, normalized]
        : previous.sponsorshipPackages.map(pkg => pkg.id === normalized.id ? normalized : pkg);
      return { ...previous,
        tickets: Number(normalized.includedTickets) > 0 && assignedTicket && !previous.tickets.some(ticket => ticket.id === assignedTicket.id)
          ? [...previous.tickets, assignedTicket] : previous.tickets,
        sponsorshipPackages,
      };
    });
    if (onDialogSaved) onDialogSaved();
    else setSaveResult(`The sponsor level ${draft.name} has been ${isNew ? "created" : "updated"} successfully.`);
    closeDraft();
  };
  const unsavedGuard = useWizardDialogUnsavedGuard({ draft, onSave: saveDraft, onLeave: closeDraft });
  const showFooterError = (showValidation && !draftValid) || (showAddOnValidation && (addOnCodeMissing || addOnDescriptionMissing));

  return (
    <>
      {/* Claude Design (8) Create Event Screen.dc.html:838-860 */}
      <section id="event-sponsorship-settings" aria-labelledby="event-sponsorship-settings-heading"
        className="flex flex-col gap-[20px] rounded-[12px] border border-[#ECECEC] bg-white px-[20px] py-[26px] shadow-[0_1px_2px_rgba(0,0,0,.04)] md:p-[28px]">
        <div className="flex flex-col gap-[4px]">
          <h3 id="event-sponsorship-settings-heading" className="text-[14px] font-semibold text-[#0A0A0A]">Sponsorship Settings</h3>
          <p className="text-[13px] text-[#0A0A0A]">Choose whether this event offers sponsorships and whether tax is charged on sponsorship sales.</p>
        </div>
        <FormControlLabel sx={{ m: 0, alignItems: "flex-start", gap: "12px", "& .MuiCheckbox-root": { p: 0, mt: "2px" } }}
          control={<Checkbox checked={!event.sponsorshipEnabled} onChange={(_, checked) => handleSponsorshipToggle(!checked)} />}
          label={<span className="block text-[14px] font-semibold text-[#0A0A0A]">Check box if you do not want to offer sponsorship opportunities for this event.</span>} />
        {event.sponsorshipEnabled && (
          <FormControlLabel sx={{ m: 0, alignItems: "flex-start", gap: "12px", "& .MuiCheckbox-root": { p: 0, mt: "2px" } }}
            control={<Checkbox checked={noSponsorshipTax} onChange={(_, checked) => setNoSponsorshipTax(checked)} />}
            label={<span className="block text-[14px] font-semibold text-[#0A0A0A]">Check box if you do not want to charge tax on sponsorship sales for this event. If you want to charge tax on any ticket(s) offered with a sponsorship package, this option will be available, and you can still apply tax to complimentary member tickets included in a sponsorship. The tax will be based on the value of a member ticket.</span>} />
        )}
        {event.sponsorshipEnabled && (
          <>
            <FormControlLabel sx={{ m: 0, alignItems: "flex-start", gap: "12px", "& .MuiCheckbox-root": { p: 0, mt: "2px" }, "&.Mui-disabled": { opacity: 0.55, cursor: "not-allowed" } }}
              disabled={!noSponsorshipTax || !levelMemberTicketValue}
              control={<Checkbox checked={chargesComplimentaryTicketTax} onChange={(_, checked) => setComplimentaryTicketTax(checked)} />}
              label={<span className="flex flex-col gap-[2px]">
                <span className="text-[14px] font-semibold text-[#0A0A0A]">Check if tax should be charged for the complimentary ticket, based on the price of a member ticket.</span>
                <span className="text-[13px] text-[#0A0A0A]">When checked, tax on sponsorship invoices for this event is calculated on the member ticket price for each complimentary ticket included only.</span>
                {!noSponsorshipTax ? <span className="text-[12px] text-[#727272]">Available when tax is not charged on sponsorship sales for this event.</span>
                  : !levelMemberTicketValue ? <span className="text-[12px] text-[#F0202E]">Add a Member Ticket with a price in Ticket Set Up to use this option.</span> : null}
              </span>} />
            <div className="flex flex-col gap-[8px]">
              <p className="text-[14px] font-bold text-[#0A0A0A]">Note: All future events will automatically be set with the same sponsor tax rules as selected.</p>
              <p className="text-[13px] text-[#0A0A0A]">This applies to the tax settings only. Whether sponsorship opportunities are offered is set separately for each event.</p>
            </div>
          </>
        )}
      </section>

      {event.sponsorshipEnabled ? (
        <section id="event-sponsorship-levels" aria-labelledby="event-sponsorship-levels-heading">
          {/* Create Event Screen.dc.html:864-873 */}
          <h3 id="event-sponsorship-levels-heading" className="text-[14px] font-semibold text-[#0A0A0A]">Create Sponsorship Levels</h3>
          <p className="mt-1 text-[13px] text-[#0A0A0A]">Create various sponsorship packages for this event and include sponsor benefits by clicking on Add Sponsor level</p>
          <div className="mt-[16px] flex items-center gap-5">
            {/* CES:888 (EV15-41): "+ Add Sponsor Level" text glyph, no icon. */}
            <WizardActionButton tone="gold" sx={{ minHeight: 38, fontSize: 13, borderRadius: "8px", px: "16px" }} onClick={openAdd}>
              + Add Sponsor Level
            </WizardActionButton>
          </div>

          {packages.length > 0 && <><div className="mt-6">
            <h3 className="text-[14px] font-semibold leading-tight text-[#0A0A0A]">
              View Created Sponsor Levels
            </h3>
            <p className="mt-[4px] text-[13px] leading-[1.5] text-[#0A0A0A]">
              View and edit the created sponsorship levels for this event. (availability, price and sponsor benefits)
            </p>
          </div>
          {/* CES:896-911 (EV15-40): 11px/600 uppercase headers, left aligned,
              text-only Edit / Delete buttons. */}
          <TableContainer className="mt-6 rounded-[10px] border border-[#ECECEC]">
            <Table size="small" sx={{ minWidth: 640, "& .MuiTableCell-root": { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: "#0A0A0A", py: "14px" } }}>
              <TableHead sx={{ backgroundColor: "#F7F7F8" }}>
                <TableRow>
                  <TableCell className="!w-10" />
                  {["Code", "Sponsorship Level", "Tickets Included", "Sponsor Price", "Available Spots", "Total Tickets Allocated", "Actions"].map((label) => (
                    <TableCell key={label} className={levelTableHeadCellClassName}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg, index) => {
                  const reorder = reorderLevelRow(index);
                  return (
                  <TableRow
                    key={pkg.id}
                    {...eventReorderTableRowProps(reorder.rowProps)}
                  >
                    <TableCell>
                      <EventReorderHandle {...reorder.handleProps} />
                    </TableCell>
                    <TableCell className="!font-semibold">{pkg.code}</TableCell>
                    <TableCell className="!font-semibold">{pkg.name}</TableCell>
                    <TableCell>{Number(pkg.includedTickets) || 0}</TableCell>
                    <TableCell className="!font-bold">{formatDollars(pkg.price)}</TableCell>
                    <TableCell>{Number(pkg.maxAvailable) || 0}</TableCell>
                    <TableCell>{totalAllocated(pkg)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <button type="button" className={levelTableActionClassName("bg-[#E1A731]")} onClick={() => openEdit(pkg)}>
                          Edit
                        </button>
                        <button type="button" className={levelTableActionClassName("bg-[#F21B1B]")} onClick={() => setPendingDelete(pkg)}>
                          Delete
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer></>}
        </section>
      ) : null}

      <ConfirmDialog
        open={confirmDisableOpen}
        tone="warning"
        title="Turn off sponsorship opportunities?"
        message="Turning this off stops sponsorship sales on your website. Your sponsorship levels and the sponsorships already sold are kept."
        confirmLabel="Yes, turn off"
        onConfirm={() => {
          setConfirmDisableOpen(false);
          update("sponsorshipEnabled", false);
        }}
        onCancel={() => setConfirmDisableOpen(false)}
      />

      {/* Shared row delete pop-up and success message (Create Event
          Screen.dc.html:1298-1316, 1808-1816, 2595). */}
      <DeleteConfirmModal
        open={Boolean(pendingDelete)}
        heading="Delete Sponsor Level"
        name={pendingDelete?.name || "this sponsor level"}
        onConfirm={() => {
          if (pendingDelete) {
            update(
              "sponsorshipPackages",
              packages.filter((item) => item.id !== pendingDelete.id)
            );
            setDeletedRow(deletedMessage("Sponsor Level", pendingDelete.name || "this sponsor level"));
          }
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
      <SuccessPopup open={Boolean(deletedRow)} title={deletedRow?.message || ""} highlights={deletedRow?.highlights} onClose={() => setDeletedRow(null)} />

      <ResultModal open={Boolean(saveResult)} title={saveResult || ""} onClose={() => setSaveResult(null)} />
      {/* Add / Edit sponsor level dialog */}
      <Dialog open={Boolean(draft)} onClose={unsavedGuard.requestClose} disableEnforceFocus={unsavedGuard.promptOpen} maxWidth={false} fullWidth PaperProps={{ sx: { maxWidth: "clamp(760px, 88vw, 1240px)", maxHeight: "92vh", width: "calc(100% - 32px)", mx: 2, borderRadius: "14px", "& .font-semibold": { fontWeight: 600 }, "& input": { fontSize: 14 }, "& .MuiInputBase-root.Mui-disabled": { bgcolor: "#F3F4F6", "& fieldset": { borderColor: "#ECECEC" } }, "& input.Mui-disabled": { WebkitTextFillColor: "#727272" }, "& p": { color: "#0A0A0A" } } }}>
        {/* Create Event Screen.dc.html:1391-1564: DialogContent is the scroll
            container, so the Save footer sticks to its bottom edge (kept by
            decision Q26; the design footer is not sticky). */}
        <DialogContent className="flex flex-col gap-[22px] !p-[20px] md:!p-[32px]">
          <WizardDialogHeader
            title={isNew ? "Create Sponsor Levels" : "Edit Sponsor Level"}
            subtitle="Add sponsorship types by selecting and editing a pre-made option or creating a new sponsorship type."
            onClose={unsavedGuard.requestClose}
          />
          {draft ? (
            <>
              {/* Create Event Screen.dc.html:1403-1431. Sponsor Code is optional
                  (no star, never validated). */}
              <div className="grid grid-cols-1 gap-[16px] md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-[auto_auto_auto_auto]">
                <EventLibrarySelect chamberId={chamberId} kind={CmpEventLibraryKindEnum.SponsorName} label="Sponsor Name" hint="Appears on the event page for sponsors to select." placeholder="Add new or select premade sponsor name" searchPlaceholder="Search Sponsor Name" error={showValidation && !draft.name.trim() ? enterFieldMessage("Sponsor Name") : undefined} required compact value={draft.name} pairedValue={draft.code} onChange={(value, pair) => patchDraft({ name: value, code: pair ?? draft.code })} />
                <EventLibrarySelect chamberId={chamberId} kind={CmpEventLibraryKindEnum.SponsorCode} label="Sponsor Code" hint="Add optional sponsor code." placeholder="Add new or select premade sponsor code" searchPlaceholder="Search Sponsor Code" compact value={draft.code || ""} pairedValue={draft.name} onChange={(value, pair) => patchDraft({ code: value, name: pair || draft.name })} />
                <div className="grid grid-cols-[minmax(0,1fr)] content-start gap-[6px] md:row-span-4 md:grid-rows-subgrid">
                  <FieldLabel compact fontSize={13} className="contents [&>span]:leading-[normal]" hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]" hint="Set the price for this sponsorship." required>Sponsor Price</FieldLabel>
                  {/* POL-005: shared CurrencyInput (canonicalizes on blur, so
                      the old formatDollars onBlur hook is gone); verbatim
                      "Enter Price" placeholder; wizard-skin overrides keep the
                      58px MUI-outline look, with a red border standing in for
                      the MUI error state. */}
                  <CurrencyInput
                    ariaLabel="Sponsor Level Price"
                    placeholder="0.00"
                    value={draft.price}
                    onChange={(value) => patchDraft({ price: value })}
                    inputClassName={`h-[42px] w-full rounded-md border border-[#9E9E9E] bg-white pl-8 pr-4 text-sm text-gray-900 outline-none focus:border-cpaGold ${
                      showValidation && !draft.price.trim()
                        ? "!border-red-500"
                        : ""
                    }`}
                    adornmentClassName={wizardCurrencyAdornmentClassName}
                  />
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)] content-start gap-[6px] md:row-span-4 md:grid-rows-subgrid">
                  <FieldLabel compact fontSize={13} className="contents [&>span]:leading-[normal] [&>span]:whitespace-nowrap" hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]" hint="Enter the number of available spots for this sponsorship level." required>Sponsorships Available</FieldLabel>
                  <QuantityStepper
                    value={draft.maxAvailable}
                    onChange={(next) => patchDraft({ maxAvailable: next })}
                    disabled={titleSponsorSpotsLocked}
                    ariaLabel="sponsorships available"
                    error={showValidation && (!Number.isInteger(Number(draft.maxAvailable)) || Number(draft.maxAvailable) <= 0)}
                  />
                </div>
              </div>

              <div>
                <FieldLabel compact hint="If this sponsorship level includes complimentary no-charge tickets, assign the number which are included for this sponsorship. Tickets allocated with this sponsorship will show up on the sponsorship invoice at no charge. If left blank, no tickets will be assigned for this sponsorship.">
                  Assign Event Tickets (if applicable)
                </FieldLabel>
                {/* July 9, 2026 direction: only the No Charge Sponsorship
                    Ticket (NCST) can be assigned, so Code/Description/Price
                    are all greyed-out read-only and only the quantity is
                    editable. Code and Qty stay narrow so Description gets
                    the width. */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[150px_minmax(0,1fr)_130px]">
                  <div>
                    <FieldLabel compact fontSize={13} className="mb-[6px]">Code</FieldLabel>
                    <TextField
                      fullWidth
                      disabled
                      value={assignedTicket?.code || ""}
                      sx={[wizardTextFieldSx, { "& .MuiOutlinedInput-root": { height: 42 }, "& input": { fontSize: 14 } }]}
                    />
                  </div>
                  <div>
                    <FieldLabel compact fontSize={13} className="mb-[6px]">Description</FieldLabel>
                    <TextField
                      fullWidth
                      disabled
                      value={assignedTicket?.name || ""}
                      sx={[wizardTextFieldSx, { "& .MuiOutlinedInput-root": { height: 42 }, "& input": { fontSize: 14 } }]}
                    />
                  </div>
                  <div>
                    <FieldLabel compact fontSize={13} className="mb-[6px]">Ticket Qty</FieldLabel>
                    <QuantityStepper
                      value={draft.includedTickets}
                      onChange={(next) => patchDraft({ includedTickets: next })}
                      ariaLabel="ticket quantity"
                      disabled={!assignedTicket}
                    />
                  </div>

                </div>

                {!assignedTicket ? (
                  <p className="mt-2 text-[14px] leading-6 text-gray-500">
                    This event has no No Charge Sponsorship Ticket (NCST). Add a ticket with code NCST in Ticket Set Up to include tickets with this sponsorship.
                  </p>
                ) : null}
              </div>

              {/* Design (15) CES:1433-1508: Assign Event Tickets sits directly
                  under the price row, then this heading and the check boxes
                  (Monday 13255196319). */}
              <h4 className="m-0 text-[16px] font-semibold leading-[21px] text-[#0A0A0A]">Please check off the appropriate boxes for this event</h4>

              <>
                {/* EV-N4: checked = hero banner on (heroBannerEnabled keeps its
                    stored meaning). Copy: Create Event Screen.dc.html:1490.
                    Taken: checkbox and label at opacity .45, cursor
                    not-allowed (CES:1488-1489, 2837-2838; Monday 13291628034). */}
                <FormControlLabel className="!m-0 !items-start !gap-[12px]" disabled={heroTaken} data-testid="title-sponsor-option"
                  sx={{ "& .MuiCheckbox-root": { p: 0, mt: "2px" }, ...(heroTaken ? { opacity: 0.45, "&, &.Mui-disabled, & .MuiCheckbox-root, & .MuiFormControlLabel-label": { cursor: "not-allowed" } } : {}) }}
                  control={<Checkbox checked={Boolean(draft.heroBannerEnabled)}
                  onChange={(_, checked) => handleHeroBannerToggle(checked)} />}
                  label={<span className="text-[14px] font-bold text-[#0A0A0A]">Check this box to make this sponsor level the exclusive Title Sponsor displayed on the left side of the event countdown banner. Only one Title Sponsor is allowed. Once sold, the sponsor’s company logo will be displayed.</span>} />
                {/* Direct children of the 22px-gap dialog column, like the
                    design, so margin -8px 0 0 30px (CES:1494) gives its spacing. */}
                {heroTaken ? (
                  <p className="m-0 text-[13px] text-[#727272]" style={{ margin: "-8px 0 0 30px" }}>
                    The Title Sponsor is already assigned to {heroOwner?.name}. Uncheck it on that sponsor level to make it available here.
                  </p>
                ) : null}
                {titleSponsorSpotsNote ? (
                  <p className="m-0 text-[13px] font-semibold text-[#B45309]" style={{ margin: "-8px 0 0 30px" }}>
                    Only one Title Sponsor is allowed. Set Sponsorships Available to 1.
                  </p>
                ) : null}
              </>

              {/* Create Event Screen.dc.html:1461-1472 (Monday 13211717938): ticked
                  means hidden. "Event page" drives publicDisplayEnabled; the
                  Sponsorships & Advertising page drives websiteListingEnabled.
                  A level stays buyable while either place shows it. */}
              <FormControlLabel className="!m-0 !items-start !gap-[12px]" sx={{ "& .MuiCheckbox-root": { p: 0, mt: "2px" } }}
                control={<Checkbox checked={draft.publicDisplayEnabled === false} onChange={(_, hidden) => patchDraft({ publicDisplayEnabled: !hidden })} />}
                label={<span className="text-[14px] font-bold text-[#0A0A0A]">Check this box to hide this sponsorship from the event page.</span>} />
              <FormControlLabel className="!m-0 !items-start !gap-[12px]" sx={{ "& .MuiCheckbox-root": { p: 0, mt: "2px" } }}
                control={<Checkbox checked={draft.websiteListingEnabled === false} onChange={(_, hidden) => patchDraft({ websiteListingEnabled: !hidden })} />}
                label={<span className="flex flex-col gap-[2px]"><span className="text-[14px] font-bold text-[#0A0A0A]">Check this box to hide this sponsorship from the Sponsorships &amp; Advertising page of your website.</span><span className="text-[13px] font-normal text-[#0A0A0A]">Event sponsorships are automatically removed from your website when the event ends.</span></span>} />

              <div>
                <FieldLabel
                  required
                  hint="To help sell this sponsorship, list all benefits and recognition details sponsors will receive for this sponsorship, which will be shown on both the event page and sponsorship and advertising page on the website if not restricted above."
                >
                  Additional Sponsorship Benefits
                </FieldLabel>
                {/* Mock toolbar: Paragraph | B I U | bullet list | align |
                    link — the shared editor's bullet-list variant. */}
                <div className={showValidation && !stripHtml(draft.additionalBenefits || "") ? "rounded-[8px] outline outline-1 outline-red-500" : undefined}>
                <RichTextEditor variant="event"
                  value={draft.additionalBenefits || ""}
                  onChange={(html) => patchDraft({ additionalBenefits: html })}
                  placeholder="Enter Sponsorship Benefits"
                  maxLength={2500}
                  minHeight={120}
                  withBulletList
                />
                </div>
                {showValidation && !stripHtml(draft.additionalBenefits || "") && <p className="mt-[6px] text-[12px] !text-[#F0202E]">Sponsorship benefits are required</p>}
              </div>

              <div>
                <FieldLabel compact hint="Add an additional sponsorship add-ons, and appropriate quantities included with this sponsorship package, this could be booth displays, advertising, meals, etc. If left blank nothing will appear on website.">
                  Sponsorship Add-ons
                </FieldLabel>
                <WizardActionButton
                  tone="gold"
                  sx={{ minHeight: 38, fontSize: 13, px: "16px" }}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setShowAddOnValidation(false);
                    setAddOnEntry(addOnEntry ? null : { id: wizardId("addon"), description: "", code: "", quantity: "0" });
                  }}
                >
                  Add Sponsorship Add-ons
                </WizardActionButton>
                {addOnEntry ? (
                  <div className="mt-[10px] rounded-[12px] border border-[#ECECEC] bg-[#FCFCFC] p-[18px]">
                    {/* July 9, 2026 direction: Code renders before
                        Description, matching the add-ons list columns. */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[150px_minmax(0,1fr)_140px_90px] md:items-end">
                      <div>
                        <FieldLabel compact fontSize={13} className="mb-[6px]">Code</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder="Enter Code"
                          inputProps={{ maxLength: 4 }}
                          value={addOnEntry.code}
                          onChange={({ target }) =>
                            setAddOnEntry({ ...addOnEntry, code: target.value })
                          }
                          error={showAddOnValidation && addOnCodeMissing}
                          sx={[wizardTextFieldSx, { "& .MuiOutlinedInput-root": { height: 42 }, "& input": { fontSize: 14 } }]}
                        />
                      </div>
                      <div>
                        <FieldLabel compact fontSize={13} className="mb-[6px]">Description</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder="Enter Description"
                          value={addOnEntry.description}
                          onChange={({ target }) =>
                            setAddOnEntry({ ...addOnEntry, description: target.value })
                          }
                          error={showAddOnValidation && addOnDescriptionMissing}
                          sx={[wizardTextFieldSx, { "& .MuiOutlinedInput-root": { height: 42 }, "& input": { fontSize: 14 } }]}
                        />
                      </div>
                      <div>
                        <FieldLabel compact fontSize={13} className="mb-[6px]">Quantity</FieldLabel>
                        <QuantityStepper
                          value={addOnEntry.quantity}
                          onChange={(next) => setAddOnEntry({ ...addOnEntry, quantity: next })}
                          ariaLabel="add-on quantity"
                        />
                      </div>
                      <WizardActionButton
                        tone="gold"
                        sx={{ minHeight: 42, fontSize: 13, borderRadius: "8px", px: 0 }}
                        onClick={saveAddOnEntry}
                      >
                        Save
                      </WizardActionButton>
                    </div>
                  </div>
                ) : null}
              </div>

              {draftAddOns.length > 0 && <div>
                <FieldLabel compact hint="The list of sponsorship add-ons below will appear on website.">
                  Sponsorship Add-ons List
                </FieldLabel>
                <TableContainer className="max-w-[760px] rounded-[10px] border border-[#ECECEC]" sx={{ "& th": { fontSize: 11 }, "& td": { fontSize: 13 } }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead className="bg-gray-50">
                      <TableRow>
                        <TableCell className="!w-10" />
                        <TableCell className="!font-bold uppercase">Code</TableCell>
                        <TableCell className="!font-bold uppercase">
                          Add-ons Description
                        </TableCell>
                        <TableCell className="!font-bold uppercase">
                          Quantity
                        </TableCell>
                        <TableCell className="!font-bold uppercase">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {draftAddOns.map((addOn, index) => {
                        const reorder = reorderAddOnRow(index);
                        return (
                        <TableRow
                          key={addOn.id}
                          {...eventReorderTableRowProps(reorder.rowProps)}
                        >
                          <TableCell>
                            <EventReorderHandle {...reorder.handleProps} />
                          </TableCell>
                          <TableCell className="!font-bold !text-gray-950">{addOn.code}</TableCell>
                          <TableCell className="!font-semibold !text-gray-950">
                            {addOn.description}
                          </TableCell>
                          <TableCell className="!font-semibold">{addOn.quantity}</TableCell>
                          {/* Design: the Delete button sits at the start of the
                              Actions column (justify-self:start). */}
                          <TableCell>
                            <WizardActionButton
                              tone="red"
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              sx={{ minHeight: 0, py: "7px", fontSize: 11, px: "12px", borderRadius: "6px", backgroundColor: "#F21B1B", "& .MuiButton-startIcon": { mr: "5px", "& svg": { fontSize: 12 } } }}
                              onClick={() => setPendingAddOnRemoval(addOn)}
                            >
                              Delete
                            </WizardActionButton>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>}

              <div className="sticky bottom-[-20px] z-[2] -mx-[20px] -mb-[20px] flex flex-wrap items-center justify-end gap-[16px] border-t border-[#ECECEC] bg-white px-[20px] py-[14px] md:bottom-[-32px] md:-mx-[32px] md:-mb-[32px] md:px-[32px]">
                {showFooterError ? (
                  <p role="alert" className="m-0 mr-auto text-[13px] !text-[#F0202E]">
                    Please complete the highlighted required fields.
                  </p>
                ) : null}
                <WizardActionButton tone="gold" sx={{ minHeight: 0, py: "13px", px: "44px", fontSize: 14, borderRadius: "8px", lineHeight: "normal" }} onClick={saveDraft}>
                  Save
                </WizardActionButton>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
      {unsavedGuard.prompt}
      <DeleteConfirmModal open={Boolean(pendingAddOnRemoval)} heading="Delete Add-on"
        name={pendingAddOnRemoval?.description || pendingAddOnRemoval?.code || "add-on"} onCancel={() => setPendingAddOnRemoval(null)} onConfirm={() => {
          patchDraft({ addOns: draftAddOns.filter(item => item.id !== pendingAddOnRemoval?.id) });
          // Create Event Screen.dc.html:1816, 2784: the add-on success message.
          if (pendingAddOnRemoval) setDeletedRow(deletedMessage("Add-on", pendingAddOnRemoval.description || pendingAddOnRemoval.code || "add-on"));
          setPendingAddOnRemoval(null);
        }} />
    </>
  );
};

export default Step4SponsorSetup;
